import { userApiClient, ApiError } from '../api/client';

/**
 * PasswordResetService - Handles password reset flow
 *
 * Responsibilities:
 * - Request password reset email
 * - Set new password with reset token
 *
 * Note: These operations don't require authentication as they use
 * time-limited tokens sent via email.
 */
class PasswordResetService {
  /**
   * Request a password reset email
   *
   * @param email - The user's email address
   * @throws {ApiError} If the request fails
   */
  async requestReset(email: string): Promise<void> {
    try {
      await userApiClient.post(
        '/api/v1/auth/request-password-reset',
        { email },
        { skipAuth: true },
      );
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Password reset request failed', 0, 'UnknownError', error);
    }
  }

  /**
   * Set a new password using a reset token
   *
   * @param password - The new password
   * @param token - The reset token from the email link
   * @throws {ApiError} If the password reset fails
   */
  async setNewPassword(password: string, token: string): Promise<void> {
    try {
      await userApiClient.post(
        '/api/v1/auth/reset-password',
        { password, token },
        { skipAuth: true },
      );
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Password reset failed', 0, 'UnknownError', error);
    }
  }
}

// Export singleton instance
export const passwordResetService = new PasswordResetService();
